import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuItem,
} from "@/components/UI/DropdownMenu";
import { useUser } from "@/contexts/UserContext";
import { ROUTES } from "@/constants/navigation";
import { useClipboardCopy } from "@/hooks/useClipboardCopy";
import { useProjects } from "@/hooks/useProjects";
import { refreshAccessTokenForProject } from "@/services/authRefreshSession";
import "./AccountProjectSelector.css";

const PROJECT_MENU_ID = "account-project-selector";

export function AccountProjectSelector() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { session } = useUser();
  const { projects } = useProjects({ enabled: session.isAuthenticated });
  const { copy, status: copyStatus } = useClipboardCopy();
  const [menuState, setMenuState] = useState<"closed" | "open">("closed");
  // Undefined until a project is picked here; null is the choice of none.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>();
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const open = menuState === "open";

  const effectiveProjectId = selectedProjectId === undefined
    ? session.projectId
    : selectedProjectId;
  const isAdministrator = session.user?.username === "admin";
  const currentProject = projects.find((project) => project.uuid === effectiveProjectId) ?? null;

  const closeMenu = () => setMenuState("closed");
  const toggleMenu = () => {
    setMenuState((currentState) => (
      currentState === "open" ? "closed" : "open"
    ));
  };

  const selectProject = async (projectId: string | null) => {
    const projectChanged = projectId !== effectiveProjectId;
    closeMenu();
    const refreshed = await refreshAccessTokenForProject(projectId);
    if (!refreshed) return;

    setSelectedProjectId(projectId);
    await session.refreshMe();
    if (projectChanged) navigate(ROUTES.account);
  };

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) {
        closeMenu();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      closeMenu();
      buttonRef.current?.focus();
    };

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div
      className={`account-project ${open ? "account-project--open" : ""}`}
      ref={rootRef}
    >
      <button
        ref={buttonRef}
        type="button"
        className="account-project__trigger"
        aria-label={t("accountLayout.projectSelector")}
        aria-controls={open ? PROJECT_MENU_ID : undefined}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={toggleMenu}
      >
        <span className="account-project__identity">
          <span className="account-project__label">
            {t("accountLayout.projectLabel")}
          </span>
          <span className="account-project__name">{currentProject?.name
            ?? (effectiveProjectId ? "" : t("accountLayout.noProject"))}</span>
        </span>
        <span className="account-project__chevron" aria-hidden="true">
          <AccountProjectChevron />
        </span>
      </button>
      {currentProject ? (
        <div className="account-project__uuid-row">
          <span className="account-project__uuid">{currentProject.uuid}</span>
          <button
            type="button"
            className="account-project__copy"
            aria-label={t(
              copyStatus === "copied"
                ? "a11y.copiedToClipboard"
                : copyStatus === "failed"
                  ? "a11y.copyFailedToClipboard"
                  : "a11y.copyToClipboard",
            )}
            onClick={() => void copy(currentProject.uuid)}
          >
            <AccountProjectCopyIcon />
          </button>
        </div>
      ) : null}
      <span className="visually-hidden" role="status">
        {copyStatus === "idle"
          ? ""
          : t(
              copyStatus === "copied"
                ? "a11y.copiedToClipboard"
                : "a11y.copyFailedToClipboard",
            )}
      </span>

      {open ? (
        <DropdownMenu
          id={PROJECT_MENU_ID}
          label={t("accountLayout.projectSelector")}
          className="account-project__menu"
        >
          {projects.map((project) => (
            <DropdownMenuItem
              key={project.uuid}
              checked={project.uuid === currentProject?.uuid}
              onClick={() => selectProject(project.uuid)}
            >
              {project.name || "—"}
            </DropdownMenuItem>
          ))}
          {isAdministrator ? (
            <DropdownMenuItem
              checked={!effectiveProjectId}
              onClick={() => selectProject(null)}
            >
              {t("accountLayout.noProject")}
            </DropdownMenuItem>
          ) : null}
        </DropdownMenu>
      ) : null}
    </div>
  );
}

function AccountProjectCopyIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M15 9V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h4" />
    </svg>
  );
}

function AccountProjectChevron() {
  return (
    <svg width="9" height="5" viewBox="0 0 9 5" fill="none" aria-hidden>
      <path d="M1 1L4.5 4L8 1" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}